// src/apps/tasks/TasksApp.tsx
import { useMemo } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { HOME_ROUTE } from '@/apps/overview/route';
import { ListsProvider } from '@/contexts/ListsContext';
import { NotesProvider } from '@/contexts/NotesContext';
import { GoalsProvider } from '@/contexts/GoalsContext';
import { AppShellChromeContext, type AppShellChromeContextValue } from '@/contexts/AppShellChromeContext';
import { useScratchpadHidden } from '@/hooks/useScratchpadHidden';
import { HomeViewContainer } from './HomeViewContainer';
import { InboxViewContainer } from './InboxViewContainer';
import { TaskViewRoute } from './TaskViewRoute';
import { SomedayPage } from './SomedayPage';

// TasksApp is the index app at `/` (after the P4.8 cutover). Its inner
// Routes handle both:
// - the cutover paths: `/`, `/today`, `/inbox`, `/task/:taskId`
// - the legacy parallel paths under `/tasks-new/*` (kept for rollback
//   safety until P5 cleanup): `tasks-new`, `tasks-new/today`,
//   `tasks-new/inbox`, `tasks-new/task/:taskId`
//
// Both branches use the same containers (HomeViewContainer, InboxViewContainer,
// TaskViewRoute), so behavior is identical. The cutover is gated by a
// localStorage feature flag in main.tsx; with the flag OFF the App.tsx
// legacy mount handles /today, /inbox, /task/:id and Shell isn't reached
// for those paths.

export function TasksApp() {
  // HomeHeader (rendered by HomeView) consumes AppShellChrome. The Today AI
  // button toggles the right-rail assistant, whose shared visibility lives in
  // useScratchpadHidden (the same state Shell.tsx's ShellAssistantHost reads),
  // so the masthead button and the rail stay in sync.
  const { hidden, setHidden } = useScratchpadHidden();
  const chrome = useMemo<AppShellChromeContextValue>(
    () => ({
      chatOpen: !hidden,
      onChatOpenChange: (open: boolean) => setHidden(!open),
    }),
    [hidden, setHidden],
  );

  return (
    <AppShellChromeContext.Provider value={chrome}>
      <GoalsProvider>
        <ListsProvider>
          <NotesProvider>
            <Routes>
            {/* Cutover paths (active when feature flag enabled) */}
            {/* Signing in, or opening the app at its root, lands on Home (Scott,
                2026-10-14). Links to a page still go straight to it. */}
            <Route path="/" element={<Navigate to={HOME_ROUTE} replace />} />
            <Route path="today" element={<HomeViewContainer key="today" />} />
            <Route path="inbox" element={<InboxViewContainer />} />
            <Route path="task/:taskId" element={<TaskViewRoute />} />
            {/* The Week BENCH (streamlined vision Phase 1): the provisioning
                surface — HomeView pinned to its week grid at its own route.
                Not a horizon rung; paper owns planning. */}
            <Route path="week" element={<HomeViewContainer key="week" fixedView="week" />} />
            {/* Remaining horizon pages stay de-navved (2026-08 pivot): old
                links and muscle memory land on Today instead of 404ing. The
                page components under horizons/ are parked, not deleted. */}
            <Route path="month" element={<Navigate to="/today" replace />} />
            <Route path="season" element={<Navigate to="/today" replace />} />
            <Route path="year" element={<Navigate to="/today" replace />} />
            {/* Someday has a door again: the shelf you can take things back off (2026-09-20). */}
            <Route path="someday" element={<SomedayPage />} />
            {/* Legacy parallel paths (always available; planned to remove in P5) */}
            <Route path="tasks-new" element={<Navigate to="/tasks-new/today" replace />} />
            <Route path="tasks-new/today" element={<HomeViewContainer key="today" />} />
            <Route path="tasks-new/inbox" element={<InboxViewContainer />} />
            <Route path="tasks-new/task/:taskId" element={<TaskViewRoute />} />
            </Routes>
          </NotesProvider>
        </ListsProvider>
      </GoalsProvider>
    </AppShellChromeContext.Provider>
  );
}
